import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Slide, Fade } from "@mui/material";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { Col, Container, Form, Row, Stack, InputGroup } from "react-bootstrap";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import Images from "../../../../utils/images";
import GeneralButton from "../../../common/SaveButton/GeneralButton";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { showError, showSuccess } from "../../../../utils/showMessage";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { addCloudConfiguration, getCloudConfigurationById, updateCloudConfiguration, pollCloudConfigurationStatus } from "../../../../api/cloudDiscovery";
import Select from "react-select";
import Loader from "../../../common/Loader/Loader";
import GetCapabilities from "../../../../utils/getCapabilities.js";
import { useReactSelectTheme } from "../../../../utils/useReactSelectTheme";

export default function CloudDiscoveryOperations() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const providerFromQuery = searchParams.get("provider");
  const operationType = id ? "edit" : "add";
  const [slideChecked, setSlideChecked] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [configurationName, setConfigurationName] = useState("");
  const [autoOnboard, setAutoOnboard] = useState(false);
  const [whatToScan, setWhatToScan] = useState([]);
  const [configId, setConfigId] = useState(null);
  const { height } = useWindowDimensions();
  const datatable_height = height - 120 + "px";
  const [awsAccessKeyId, setAwsAccessKeyId] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [awsRegion, setAwsRegion] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [provider, setProvider] = useState(providerFromQuery || "aws");
  const my_capabilities = GetCapabilities();
  const isCapable = my_capabilities[0];
  const { styles: selectStyles, theme: selectTheme } = useReactSelectTheme();
  const scanOptions = [
    { label: "EC2 Instance", value: "EC2", isDisabled: false },
    { label: "RDS Instance (Coming Soon)", value: "RDS", isDisabled: true },
    { label: "Virtual Private Cloud / Network (Coming Soon)", value: "VPC", isDisabled: true },
    { label: "S3 Bucket (Coming Soon)", value: "S3", isDisabled: true },
    { label: "Lambda (Coming Soon)", value: "LAMBDA", isDisabled: true },
    { label: "EKS Cluster (Coming Soon)", value: "EKS", isDisabled: true },
    { label: "ECS Cluster (Coming Soon)", value: "ECS", isDisabled: true },
    { label: "DynamoDB Table (Coming Soon)", value: "DYNAMODB", isDisabled: true },
    { label: "CloudWatch Log Group (Coming Soon)", value: "CLOUDWATCH", isDisabled: true },
  ];
  const handleSelectChange = (selectedOptions) => {
    const enabledOptions = (selectedOptions || []).filter(
      (option) => !option.isDisabled && (option.value === "EC2" || option.value === "RDS")
    );
    setWhatToScan(enabledOptions); 
  };

  const awsRegionNames = {
    "us-east-1": "N. Virginia",
    "us-east-2": "Ohio",
    "us-west-1": "N. California",
    "us-west-2": "Oregon",
    "ap-south-1": "Mumbai",
    "ap-southeast-1": "Singapore",
    "ap-southeast-2": "Sydney",
    "ap-northeast-1": "Tokyo",
    "eu-central-1": "Frankfurt",
    "eu-west-1": "Ireland",
    "eu-west-2": "London",
  };

  const awsRegions = [
    "us-east-1",
    "us-east-2",
    "us-west-1",
    "us-west-2",
    "ap-south-1",
    "ap-southeast-1",
    "ap-southeast-2",
    "ap-northeast-1",
    "eu-central-1",
    "eu-west-1",
    "eu-west-2",
  ];

  const validateProviderFields = () => {
    if (!configurationName) {
      showError("Please enter a configuration name");
      return false;
    }
    if (!awsAccessKeyId) {
      showError("Please enter AWS Access Key ID");
      return false;
    }
    if (!awsSecretAccessKey) {
      showError("Please enter AWS Secret Access Key");
      return false;
    }
    if (!awsRegion) {
      showError("Please select an AWS Region");
      return false;
    }
    if (whatToScan.length === 0) {
      showError("Please select at least one Scan Target");
      return false;
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!provider) {
      showError("Please select a cloud provider");
      return;
    }

    if (!validateProviderFields()) {
      setValidated(true);
      return;
    }

    if (operationType === "edit" && !configId) {
      showError("Missing configuration ID for editing");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        configuration_name: configurationName,
        platform: provider,
        what_to_scan: whatToScan.map((item) => item.value),
        auto_onboard: autoOnboard,
        aws_access_key_id: awsAccessKeyId,
        aws_secret_access_key: awsSecretAccessKey,
        aws_region: awsRegion?.value || null,
      };

      let response;
      let savedConfigId = null;
      
      if (operationType === "edit" && configId) {
        response = await updateCloudConfiguration({ config_id: configId, ...payload });
        savedConfigId = response.data?.config?.id || configId;
      } else {
        response = await addCloudConfiguration(payload);
        if (response.data?.config?.id) {
          savedConfigId = response.data.config.id;
        }
      }
      const { data, error } = response;

      if (error) {
        showError(error?.errors?.message?.[0] || (typeof error === 'string' ? error : null) || "Failed to save configuration");
      } else {
        showSuccess(
          data?.message ||
          (operationType === "edit"
            ? "Configuration updated successfully"
            : "Configuration added successfully")
        );
        
        const configProvider = data?.config?.provider_type || data?.config?.platform || data?.provider_type || provider;
        const providerType = typeof configProvider === 'string' ? configProvider.toLowerCase() : 'aws';
        
        navigate("/dashboard/discovery/cloudconfiguration", { 
          state: { shouldRefresh: true, configId: savedConfigId } 
        });
        
        if (savedConfigId && providerType === 'aws') {
          setTimeout(async () => {
            try {
              await pollCloudConfigurationStatus({ config_id: savedConfigId });
              window.dispatchEvent(new CustomEvent('cloudConfigUpdated', { 
                detail: { configId: savedConfigId } 
              }));
            } catch (pollError) {
              console.error("Failed to poll status after save/update:", pollError);
            }
          }, 1000);
        }
      }
    } catch (err) {
      showError("Failed to save configuration");
    } finally {
      setLoading(false);
    }
  };

  const cancelAction = () => {
    navigate("/dashboard/discovery/cloudconfiguration");
  };

  useEffect(() => {
    document.title = operationType === "edit" ? "Edit Cloud Configuration" : "Add Cloud Configuration";
    setSlideChecked(true);
    
    if (operationType === "edit" && !isCapable?.cloud_discovery?.edit) {
      navigate("/dashboard/discovery/cloudconfiguration");
      return;
    }
    if (operationType === "add" && !isCapable?.cloud_discovery?.add) {
      navigate("/dashboard/discovery/cloudconfiguration");
      return;
    }
    
    const init = async () => {
      if (operationType === "edit" && id) {
        try {
          const { data, error } = await getCloudConfigurationById({ config_id: id });
          if (error) {
            showError(error?.message?.[0] || "Failed to load cloud configuration");
            return;
          }
          if (data) {
            const cfg = data;
            setConfigId(cfg.id || "");
            setConfigurationName(cfg.configuration_name || cfg.name || "");
            setProvider(providerFromQuery || cfg.platform || "aws");
            setAutoOnboard(false);
            setWhatToScan(
              (cfg.what_to_scan || cfg.service || cfg.services || [])
                .map((val) =>
                  scanOptions.find((opt) => opt.value === (typeof val === "string" ? val : val.value))
                )
                .filter(Boolean)
                .filter((opt) => !opt.isDisabled && (opt.value === "EC2" || opt.value === "RDS"))
            );
            setAwsAccessKeyId(cfg.aws_access_key_id || "");
            setAwsSecretAccessKey(cfg.aws_secret_access_key || "");
            const regions = cfg.aws_region || cfg.region || cfg.regions || [];
            const regionValue = Array.isArray(regions) ? regions[0] : regions;
            if (regionValue) {
              const regionCode = typeof regionValue === "string" ? regionValue : (regionValue.value || regionValue);
              const regionName = awsRegionNames[regionCode] || regionCode;
              setAwsRegion({
                label: `${regionCode} - ${regionName}`,
                value: regionCode
              });
            } else {
              setAwsRegion(null);
            }
          }
        } catch (e) {
          showError("Failed to load cloud configuration: " + (e.message || String(e)));
        }
      } else {
        setProvider(providerFromQuery || "aws");
      }
    };

    init();
  }, [operationType, id, providerFromQuery, isCapable, navigate]);

  return (
    <>
      <Loader loading={loading} bg_papper={false} />
      <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
        <div>
          <Fade timeout={fadedelayTime} in={slideChecked}>
          <Container fluid
            className="main_content_container mx-auto p-3">
            <div
              className="position-relative bg-white"
              style={{
                height: datatable_height,
                overflowY: "auto", overflowX: "hidden"
              }}
            >
              <div className="configure_mfa_heading">
                <div className="ms-4 ps-1 py-2 pt-1">
                  <BreadcrumbHeading
                    dataTableHeadingText={
                      operationType === "edit"
                        ? [
                            "Cloud Discovery",
                            "Edit Cloud Configuration"
                          ]
                        : [
                            "Cloud Discovery",
                            "Select Cloud Platform",
                            "Add Cloud Configuration"
                          ]
                    }
                    dataTablePageLink={
                      operationType === "edit"
                        ? [
                            "/dashboard/discovery/cloudconfiguration",
                            `/dashboard/discovery/cloudconfiguration/edit-cloudconfiguration/${id}`,
                          ]
                        : [
                            "/dashboard/discovery/cloudconfiguration",
                            "/dashboard/discovery/cloudconfiguration/select-plateform/add-configuration",
                            "/dashboard/discovery/cloudconfiguration/add-cloudconfiguration?provider=aws",
                          ]
                    }
                    pageNumber={operationType === "edit" ? 2 : 3}
                  />
                </div>
                <div className="p-2 d-flex flex-row justify-content-between w-100 ms-2">
                  <div className="d-flex align-items-center fw-300 ff-poppins text-wrap ms-3 mb-2">
                    <ListHeading
                      dataTableHeadingText={[
                        operationType === "edit"
                          ? "Edit Cloud Configuration"
                          : "Add Cloud Configuration",
                      ]}
                      dataTableHeadingIcon={Images.NetworkAnalyzer}
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 ms-2 mt-4 overflow-auto"
               style={{ height: height - 350 }}
              >
                <Form
                  noValidate
                  validated={validated}
                  onSubmit={handleSave}
                  className="add_cloud_configuration ms-1 "
                  autoComplete="off"
                  id="cloud_discovery_operations"
                >
                  <Stack gap={1}>
                    <Row className="pb-3">
                      <Col md={5}>
                        <Form.Group controlId="configurationName" >
                          <Form.Label className="ff-pam fs-14px">
                            Configuration Name<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            className="user_input_field"
                            type="text"
                            value={configurationName}
                            onChange={(e) => setConfigurationName(e.target.value)}
                            placeholder="Enter configuration name"
                            autoComplete="new-configuration name"
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter a configuration name
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={5} className="ps-5">
                        <Form.Group controlId="awsAccessKeyId">
                          <Form.Label className="ff-pam fs-14px">
                            AWS Access Id<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            required
                            className="user_input_field"
                            type="text"
                            value={awsAccessKeyId}
                            onChange={(e) => setAwsAccessKeyId(e.target.value.toUpperCase())}
                            placeholder="Enter access key ID"
                            pattern="^(AKIA|ASIA|AGPA|AIDA)[A-Z0-9]{16}$"
                            maxLength={20}
                            minLength={20}
                            autoComplete="new-configuration name"
                          />
                          <Form.Control.Feedback type="invalid">
                            AWS Access Key ID must be exactly 20 characters, start with AKIA/ASIA/AGPA/AIDA, and contain only uppercase letters and numbers.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="pb-3">
                      <Col md={5} >
                        <Form.Group >
                          <Form.Label
                            className="ff-pam fs-14px"
                          >
                            AWS Secret Access Key
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup>
                            <Form.Control
                              required
                              className="user_input_field"
                              type={showSecretKey ? "text" : "password"}
                              value={awsSecretAccessKey}
                              onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                              placeholder="Enter secret access key"
                              pattern="^[A-Za-z0-9\/+=]{40}$"
                              maxLength={40}
                              minLength={40}
                              autoComplete="aws password"
                            />
                            <InputGroup.Text
                              className="bg-white"
                              onClick={() => setShowSecretKey(!showSecretKey)}
                              style={{ cursor: "pointer" }}
                            >
                              {showSecretKey ? (
                                <LazyLoadImage
                                  src={Images.Hide_Text_Eye_Icon}
                                  height="20px"
                                  className="px-2 eye-icon"
                                  alt="Hide password"
                                />
                              ) : (
                                <LazyLoadImage
                                  src={Images.Show_Text_Eye_Icon}
                                  height="20px"
                                  className="px-2 eye-icon"
                                  alt="Show password"
                                />
                              )}
                            </InputGroup.Text>
                          </InputGroup>
                          <Form.Control.Feedback type="invalid">
                            AWS Secret Access Key must be exactly 40 characters and contain only letters, numbers, /, +, and =.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={5} className="ps-5">
                        <Form.Group >
                          <Form.Label
                            className="ff-pam fs-14px">
                            Region Name<span className="text-danger">*</span>
                          </Form.Label>
                          <Select
                            name="awsRegion"
                            options={awsRegions.map((r) => ({ 
                              label: `${r} - ${awsRegionNames[r] || r}`, 
                              value: r 
                            }))}
                            value={awsRegion}
                            onChange={(selectedOption) => setAwsRegion(selectedOption || null)}
                            placeholder="Select region"
                            menuPlacement="bottom"
                            isClearable
                            styles={{
                              ...selectStyles,
                              control: (provided, state) => ({
                                ...selectStyles.control(provided, state),
                                minHeight: "38px",
                                fontSize: "14px",
                                paddingTop: "4px",
                                paddingBottom: "4px",
                              }),
                              menuList: (provided) => ({
                                ...selectStyles.menuList(provided),
                                maxHeight: 270,
                              }),
                            }}
                            theme={selectTheme}
                          />

                          <Form.Control.Feedback type="invalid">
                            Please select Region Name
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="pb-3">
                      <Col md={5} >
                        <Form.Group >
                          <Form.Label className="ff-pam fs-14px">
                            Scan Target<span className="text-danger">*</span>
                          </Form.Label>

                          <Select
                            isMulti
                            name="whatToScan"
                            options={scanOptions}
                            value={whatToScan}
                            onChange={handleSelectChange}
                            placeholder="Select options"
                            menuPlacement="bottom"
                            isOptionDisabled={(option) => option.isDisabled}
                            styles={{
                              ...selectStyles,
                              control: (provided, state) => ({
                                ...selectStyles.control(provided, state),
                                minHeight: "38px",
                                fontSize: "14px",
                                paddingTop: "4px",
                                paddingBottom: "4px",
                              }),
                              menuList: (provided) => ({
                                ...selectStyles.menuList(provided),
                                maxHeight: 160,
                              }),
                              option: (provided, state) => ({
                                ...selectStyles.option(provided, state),
                                opacity: state.isDisabled ? 0.5 : 1,
                                cursor: state.isDisabled ? 'not-allowed' : 'default',
                              }),
                            }}
                            theme={selectTheme}
                          />
                          <Form.Control.Feedback type="invalid">
                            Please select what to scan
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Stack>
                </Form>
              </div>
              <div className="border-top p-3 d-flex justify-content-end">
                <GeneralButton
                  color="#505050"
                  variant="outlined"
                  value="Cancel"
                  size="large"
                  onClickEvent={cancelAction}
                  className="me-3"
                />
                <GeneralButton
                  type="submit"
                  value={operationType === "edit" ? "Update" : "Save"}
                  variant="contained"
                  size="large"
                  disabled={loading}
                  className="me-3"
                  form="cloud_discovery_operations"
                />
              </div>
            </div>
          </Container>
        </Fade>
      </div>
    </Slide>
    </>
  );
}
